// Candidate filtering, consumption tracking, and pricing helpers.

import { absRound, candidateAbsNet, isSameMonth, isSameOrAdjacentMonth } from '../../../calc.ts';
import type { MatchCandidate, UsedCandidates, FilteredCandidate, CandidateFilters } from './types.ts';
import { normVat } from './verify.ts';

// Reference tokenization/matching (extractRefs / containsRef / REF_PATTERNS) lives in the
// single reference module — `shared/services/invoice/references.ts`.

/** Absolute number of calendar months between two YYYY-MM-DD dates (0 = same month). */
function monthsApart(a: string, b: string): number {
  const am = parseInt(a.slice(0, 4), 10) * 12 + parseInt(a.slice(5, 7), 10);
  const bm = parseInt(b.slice(0, 4), 10) * 12 + parseInt(b.slice(5, 7), 10);
  return Math.abs(am - bm);
}

/** Check if candidate is already consumed in this pass. */
export function isUsed(used: UsedCandidates, c: MatchCandidate): boolean {
  return used.get(c.entity_id)?.has(c.side ?? '') ?? false;
}

/** Mark candidate as consumed in this pass. */
export function markUsed(used: UsedCandidates, c: MatchCandidate): void {
  let sides = used.get(c.entity_id);
  if (!sides) {
    sides = new Set();
    used.set(c.entity_id, sides);
  }
  sides.add(c.side ?? '');
}


/**
 * Filter candidates by matching criteria and sort by date proximity to sell_date.
 * Returns only candidates with computable net amounts.
 */
export function filterCandidates(
  candidates: MatchCandidate[],
  filters: CandidateFilters,
): FilteredCandidate[] {
  const result: FilteredCandidate[] = [];
  const liVat = normVat(filters.vat_rate_code);

  for (const c of candidates) {
    if (isUsed(filters.used, c)) continue;
    if (!c.currency || c.currency !== filters.currency) continue;
    if (filters.sell_date && c.date && c.date > filters.sell_date
        && (filters.allowAdjacentMonth ? !isSameOrAdjacentMonth(c.date, filters.sell_date) : !isSameMonth(c.date, filters.sell_date))) continue;
    // A14: LOWER date bound — reject candidates too far BEFORE sell_date. A candidate
    // more than `lookbackMonths` calendar months older than the sale can't belong to it
    // (no ref anchor in P2 to justify it). Kept wider than P1's same-month upper gate so
    // legitimately-late per-ton billing still matches.
    if (filters.lookbackMonths != null && filters.sell_date && c.date
        && c.date < filters.sell_date && monthsApart(c.date, filters.sell_date) > filters.lookbackMonths) continue;
    // Strict VAT-rate match, no '23' default (see normVat): a missing code never equals '23'.
    if (normVat(c.vat_rate_code) !== liVat) continue;
    if (filters.price_filter) {
      if (c.unit_price === null) continue;
      if (absRound(c.unit_price) !== filters.abs_unit_price) continue;
    }
    const net = candidateAbsNet(c.unit_price, c.price_type, c.weight);
    if (net === null || net === 0) continue;
    result.push({ candidate: c, net });
  }

  sortByDateProximity(result, filters.sell_date);
  return result;
}

/** Sort candidates by closeness to sell_date (most recent first). */
function sortByDateProximity(
  candidates: FilteredCandidate[],
  sellDate?: string | null,
): void {
  candidates.sort((a, b) => {
    const da = a.candidate.date ?? (sellDate ? '0000-00-00' : '9999-99-99');
    const db = b.candidate.date ?? (sellDate ? '0000-00-00' : '9999-99-99');
    return sellDate
      ? (db < da ? -1 : db > da ? 1 : 0)
      : (da < db ? -1 : da > db ? 1 : 0);
  });
}
